import { Component, computed, effect, inject, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule, Sort, SortDirection } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { Network, Period, ReviewData, ReviewsRequest } from '@models/online-reputation.models';
import { FilterStore } from '@core/stores/filter.store';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DatePipe, SlicePipe } from '@angular/common';
import { ReplyFormComponent } from './reply-form/reply-form.component';
import { TagFormComponent } from './tag-form/tag-form.component';
import { ExportHistoryDialogComponent } from './export-history/export-history-dialog.component';
import { Dispatcher } from '@ngrx/signals/events';
import { loadMoreReviews, loadReviews, searchReviews, sortReviews } from '@core/events/reviews.events';
import { ReviewsStore } from '@core/stores/reviews.store';
import { debounceTime, distinctUntilChanged, filter, map, skip, startWith } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { PageBreadcrumbComponent } from '@shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '@shared/components/common/component-card/component-card.component';
import { SearchBoxComponent } from '@shared/components/ui/search-box/search-box.component';
import { ButtonComponent } from '@shared/components/ui/button/button.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { signalMethod } from '@ngrx/signals';
import { ReviewExportStore } from '@core/stores/review-export.store';
import { startReviewExport, ReviewExportError } from '@core/events/review-export.events';
import { StickyOffsetDirective } from '@shared/directives/sticky-offset.directive';
import { MatChip, MatChipSet } from '@angular/material/chips';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'app-reviews',
  templateUrl: './reviews.component.html',
  styleUrls: ['./reviews.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTableModule,
    MatSortModule,
    MatIconModule,
    DatePipe,
    MatTooltipModule,
    MatProgressBar,
    MatDialogModule,
    TranslatePipe,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    SearchBoxComponent,
    ButtonComponent,
    StickyOffsetDirective,
    MatChipSet,
    MatChip,
    MatButton
  ]
})
export class ReviewsComponent {

  readonly displayedColumns = ['posted', 'customer', 'review', 'response', 'tags'];
  dataSource = new MatTableDataSource<ReviewData>([]);
  totalElements = 0;
  pageSize = 10;
  pageToken?: string;
  sortActive = 'commentedAt';
  sortDirection: SortDirection = 'desc';
  loading = false;
  searchText = signal('');
  readonly EDIT_THRESHOLD_MS = 60_000;
  readonly stars = [1, 2, 3, 4, 5];

  @ViewChild(MatSort) sort!: MatSort;

  readonly filterStore = inject(FilterStore);
  readonly reviewsStore = inject(ReviewsStore);
  readonly dispatcher = inject(Dispatcher);
  readonly router = inject(Router);
  readonly reviewExportStore = inject(ReviewExportStore);

  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);

  readonly downloadIcon = '<svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">'
    + '<path d="M10 3v9m0 0 3.5-3.5M10 12 6.5 8.5M4 13.5v1.75C4 16.22 4.78 17 5.75 17h8.5c.97 0 1.75-.78 1.75-1.75V13.5" '
    + 'stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  readonly spinnerIcon = '<svg class="animate-spin" width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">'
    + '<circle cx="10" cy="10" r="7" stroke="currentColor" stroke-opacity="0.25" stroke-width="2"/>'
    + '<path d="M17 10a7 7 0 0 0-7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

  readonly historyIcon = '<svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">'
    + '<path d="M3.5 10a6.5 6.5 0 1 0 1.9-4.6M3.5 3.5v2.9h2.9M10 6.5V10l2.5 1.5" '
    + 'stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /** Why the button is disabled during a search, or how far along a running export is. */
  readonly exportTooltip = computed(() => {
    if (this.reviewsStore.searchText()) {
      return this.translate.instant('app.review.export.searchNotSupported');
    }
    const total = this.reviewExportStore.totalReviews();
    if (this.reviewExportStore.inProgress() && total) {
      return this.translate.instant('app.review.export.progress', { written: this.reviewExportStore.reviewsWritten(), total });
    }
    return '';
  });

  private readonly showExportError = signalMethod<ReviewExportError | undefined>((error) => {
    if (error) {
      this.snackBar.open(this.translate.instant(error === 'tooMany' ? 'app.review.export.tooMany' : 'app.review.export.failed'));
    }
  });

  constructor() {
    this.showExportError(this.reviewExportStore.error);

    // Keep local UI state in sync with reviews store updates.
    effect(() => {
      this.totalElements = this.reviewsStore.totalElements();
      this.pageToken = this.reviewsStore.pageToken();
      this.loading = this.reviewsStore.loading();
      this.dataSource.data = this.reviewsStore.reviews();
      this.sortActive = this.reviewsStore.sortField();
      this.sortDirection = this.reviewsStore.sortDirection() === 'ASC' ? 'asc' : this.reviewsStore.sortDirection() === 'DESC' ? 'desc' : '';
    });

    toObservable(this.searchText)
      .pipe(
        skip(1),
        debounceTime(400),
        distinctUntilChanged(),
        takeUntilDestroyed()
      )
      .subscribe((text) => {
        this.dispatcher.dispatch(searchReviews({ text: text.trim() }));
      });

    this.router.events
    .pipe(
      startWith(this.router.url),
      filter((event) => typeof event === 'string' || event instanceof NavigationEnd),
      map(() => this.router.url),
      distinctUntilChanged(),
      takeUntilDestroyed(),
    )
    .subscribe(() => {
      this.fetchReviews();
    });
  }

  isEdited(original?: Date | null, updated?: Date | null): boolean {
    if (!original || !updated) return false;
    return new Date(updated).getTime() - new Date(original).getTime() > this.EDIT_THRESHOLD_MS;
  }

  openReplyForm(review: ReviewData): void {
    const filter = this.filterStore.filter();
    const network = filter.network;

    this.dialog.open(ReplyFormComponent, {
      panelClass: 'fullscreen-dialog',
      data: {
        review,
        network
      }
    }).afterClosed().subscribe((result?: { submitted?: boolean }) => {
      if (result?.submitted) {
        this.fetchReviews();
      }
    });
  }

  openTagsForm(review: ReviewData) {
    this.dialog.open(TagFormComponent, {
      width: '420px',
      maxWidth: '95vw',
      data: {
        review: review,
      }
    }).afterClosed().subscribe((result?: { submitted?: boolean }) => {
      // if (result?.submitted) {
      //   this.fetchReviews();
      // }
    });
  }

  fetchReviews(append = false): void {
    const filter = this.filterStore.filter();
    const request = this.buildRequest(
      filter.network as Network,
      filter.period as Period,
      append ? this.pageToken : undefined
    );

    if (append) {
      if (!request.pageToken) {
        return;
      }
      this.dispatcher.dispatch(loadMoreReviews(request));
      return;
    }

    this.dispatcher.dispatch(loadReviews(request));
  }

  /**
   * Exports every review for the filters and sort on screen — the same request the list is
   * loaded with, minus paging. Free-text search isn't exported, so the button is disabled while
   * one is active.
   */
  exportReviews(): void {
    const filter = this.filterStore.filter();
    this.dispatcher.dispatch(startReviewExport(this.buildRequest(filter.network as Network, filter.period as Period)));
  }

  /** The user's earlier exports that can still be downloaded. Available during a search too. */
  openExportHistory(): void {
    this.dialog.open(ExportHistoryDialogComponent, {
      width: '720px',
      maxWidth: '95vw'
    });
  }

  private buildRequest(network: Network | undefined, period: Period | undefined, pageToken?: string): ReviewsRequest {
    return {
      network: network || {} as Network,
      period: period || {} as Period,
      pageToken,
      limit: this.pageSize,
      sort: this.sortActive ? {
        field: this.sortActive,
        direction: this.sortDirection === 'asc' ? 'ASC' : this.sortDirection === 'desc' ? 'DESC' : undefined
      } : undefined,
      text: this.reviewsStore.searchText() || undefined
    };
  }

  sortData(sort: Sort): void {
    const direction = sort.direction === 'asc' ? 'ASC' : sort.direction === 'desc' ? 'DESC' : undefined;
    const field = direction ? sort.active : undefined;
    this.dispatcher.dispatch(sortReviews(field ? { field, direction } : undefined));
  }

  onSearchTextChange(value: string): void {
    this.searchText.set(value);
  }
}
